import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

const CookiePolicy = () => {
  const cookies = [
    { name: 'vett_cookie_consent', purpose: 'Stores your cookie preferences', type: 'Essential', duration: '365 days', provider: 'VETT' },
    { name: 'vett_session', purpose: 'Maintains your login session', type: 'Essential', duration: 'Session', provider: 'VETT' },
    { name: 'vett_token', purpose: 'Authentication token', type: 'Essential', duration: '7 days', provider: 'VETT' },
    { name: '_ga', purpose: 'Distinguishes users for analytics', type: 'Analytics', duration: '2 years', provider: 'Google Analytics' },
    { name: '_ga_*', purpose: 'Maintains session state for analytics', type: 'Analytics', duration: '2 years', provider: 'Google Analytics' },
    { name: '_gid', purpose: 'Distinguishes users for analytics', type: 'Analytics', duration: '24 hours', provider: 'Google Analytics' },
    { name: '_fbp', purpose: 'Tracks visits across websites for ads', type: 'Marketing', duration: '90 days', provider: 'Meta Pixel' },
    { name: '_fbc', purpose: 'Stores last visit timestamp', type: 'Marketing', duration: '90 days', provider: 'Meta Pixel' },
  ];

  return (
    <div className="min-h-screen bg-[#F5F8FF]">
      {/* Header */}
      <header className="border-b border-[#E8EDF5] bg-[#F5F8FF]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-[#1B5AFF]" />
              <span className="text-xl font-bold text-[#0A0F1E]">VETT</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-[#0A0F1E] mb-2">Cookie Policy</h1>
          <p className="text-[#5B6472] mb-8">Last Updated: March 6, 2026</p>

          <div className="prose prose-invert max-w-none space-y-8 text-[#0A0F1E]">
            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">What Are Cookies?</h2>
              <p>
                Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.
              </p>
              <p>
                Cookies allow websites to recognize your device and remember your preferences, such as login status and language settings. They can also be used to analyze how you use a website and to show you relevant advertising.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">How VETT Uses Cookies</h2>
              <p>
                VETT uses cookies to operate our platform, understand how users interact with it, and (with your consent) to serve relevant marketing. We use four categories of cookies:
              </p>
              
              <div className="mt-6 space-y-6">
                <div className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0A0F1E] mb-2">Essential Cookies</h3>
                  <p className="text-sm">
                    These cookies are necessary for the platform to function properly. They enable basic features like secure login, session management, and form submissions. Without these cookies, VETT cannot operate. These cookies cannot be disabled.
                  </p>
                </div>

                <div className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0A0F1E] mb-2">Analytics Cookies</h3>
                  <p className="text-sm">
                    These cookies help us understand how users interact with VETT so we can improve the platform. We use Google Analytics 4 to collect anonymized usage data such as pages visited, time spent, and features used. These cookies are only set if you consent.
                  </p>
                </div>

                <div className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0A0F1E] mb-2">Marketing Cookies</h3>
                  <p className="text-sm">
                    These cookies are used to show you relevant advertisements and measure the effectiveness of our marketing campaigns. We use Meta Pixel to track conversions and optimize our advertising. These cookies are only set if you consent.
                  </p>
                </div>

                <div className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#0A0F1E] mb-2">Preference Cookies</h3>
                  <p className="text-sm">
                    These cookies remember your settings and preferences to personalize your experience on VETT. For example, they may remember your preferred language or display settings. These cookies are only set if you consent.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Cookies We Use</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-[#E8EDF5] rounded-xl overflow-hidden">
                  <thead className="bg-[#FFFFFF]">
                    <tr>
                      <th className="text-left p-3 text-white border-b border-[#E8EDF5]">Cookie Name</th>
                      <th className="text-left p-3 text-white border-b border-[#E8EDF5]">Purpose</th>
                      <th className="text-left p-3 text-white border-b border-[#E8EDF5]">Type</th>
                      <th className="text-left p-3 text-white border-b border-[#E8EDF5]">Duration</th>
                      <th className="text-left p-3 text-white border-b border-[#E8EDF5]">Provider</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cookies.map((cookie, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-[#FFFFFF]/30' : ''}>
                        <td className="p-3 font-mono text-xs">{cookie.name}</td>
                        <td className="p-3">{cookie.purpose}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            cookie.type === 'Essential' ? 'bg-blue-500/20 text-blue-400' :
                            cookie.type === 'Analytics' ? 'bg-green-500/20 text-green-400' :
                            cookie.type === 'Marketing' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {cookie.type}
                          </span>
                        </td>
                        <td className="p-3">{cookie.duration}</td>
                        <td className="p-3">{cookie.provider}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Managing Your Cookie Preferences</h2>
              <p>
                When you first visit VETT, you will see a cookie consent banner that allows you to accept all cookies, manage your preferences, or reject non-essential cookies. You can change your preferences at any time by clicking the "Cookie Settings" link in the footer.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Managing Cookies in Your Browser</h2>
              <p>
                You can also manage cookies directly in your browser settings. Here's how to do it in common browsers:
              </p>
              
              <div className="mt-4 space-y-4">
                <div>
                  <h3 className="font-semibold text-white">Google Chrome</h3>
                  <p className="text-sm">Settings → Privacy and Security → Cookies and other site data</p>
                </div>
                <div>
                  <h3 className="font-semibold text-white">Mozilla Firefox</h3>
                  <p className="text-sm">Settings → Privacy & Security → Cookies and Site Data</p>
                </div>
                <div>
                  <h3 className="font-semibold text-white">Safari</h3>
                  <p className="text-sm">Preferences → Privacy → Manage Website Data</p>
                </div>
                <div>
                  <h3 className="font-semibold text-white">Microsoft Edge</h3>
                  <p className="text-sm">Settings → Cookies and site permissions → Manage and delete cookies</p>
                </div>
              </div>
              
              <p className="mt-4 text-[#1B5AFF]">
                Note: Disabling essential cookies will prevent VETT from functioning properly. You may not be able to log in or use core features.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">More Information</h2>
              <p>
                For more information about how we handle your personal data, please see our <Link to="/privacy" className="text-[#1B5AFF] hover:underline">Privacy Policy</Link>.
              </p>
              <p className="mt-4">
                If you have any questions about our use of cookies, please contact us at <a href="mailto:contact@getvett.org" className="text-[#1B5AFF] hover:underline">contact@getvett.org</a>.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E8EDF5] py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#1B5AFF]" />
              <span className="text-[#5B6472]">© 2026 VETT. All rights reserved.</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-[#5B6472] hover:text-white">Privacy Policy</Link>
              <Link to="/terms" className="text-[#5B6472] hover:text-white">Terms of Service</Link>
              <Link to="/cookies" className="text-white">Cookie Policy</Link>
              <Link to="/impressum" className="text-[#5B6472] hover:text-white">Impressum</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy;
